import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Eye, EyeOff } from "lucide-react"
import { Toast } from "primereact/toast"
import axios from "axios"
import { ClimbingBoxLoader } from "react-spinners"
import logo from "../../Assets/Images/Logo.png"

const Login = ({ onLogin }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const toast = useRef(null)

  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)

  const [showForgot, setShowForgot] = useState(false)
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState("")

  // Generate a unique session ID for this tab
  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  // Check if user is already logged in another tab
  const checkExistingSession = (username) => {
    const existingSessions = JSON.parse(localStorage.getItem("active_user_sessions") || "{}")
    console.log("Checking existing sessions for", username, ":", existingSessions)
    return existingSessions[username] || null
  }

  // Register new session in localStorage (for cross-tab communication only)
  const registerUserSession = (username, sessionId) => {
    const existingSessions = JSON.parse(localStorage.getItem("active_user_sessions") || "{}")
    existingSessions[username] = {
      sessionId,
      timestamp: Date.now(),
    }
    localStorage.setItem("active_user_sessions", JSON.stringify(existingSessions))
    console.log("Registered session for", username, "with sessionId:", sessionId)
  }

  // Listen for storage changes (when another tab logs in/out)
  useEffect(() => {
    const handleStorageChange = (e) => {
      console.log("Storage change detected in Login:", e.key, e.newValue)

      if (e.key === "active_user_sessions" && e.newValue) {
        const newSessions = JSON.parse(e.newValue)
        const currentUser = sessionStorage.getItem("current_username")
        const currentSessionId = sessionStorage.getItem("session_id")

        console.log("Active sessions changed:", newSessions)
        console.log("Current user:", currentUser, "Current session:", currentSessionId)

        if (currentUser && currentSessionId) {
          const userSession = newSessions[currentUser]

          // If there's a session for this user but it's not our session, we need to logout
          if (userSession && userSession.sessionId !== currentSessionId) {
            console.log("Session conflict detected - another tab logged in with same user")

            toast.current?.show({
              severity: "warn",
              summary: "Session Conflict",
              detail: "This user has logged in from another tab",
              life: 3000,
            })

            setTimeout(() => {
              // Clear this tab's session
              sessionStorage.clear()
              if (onLogin) onLogin()
              navigate("/")
            }, 3000)
          }
        }
      }
    }

    window.addEventListener("storage", handleStorageChange)
    return () => window.removeEventListener("storage", handleStorageChange)
  }, [onLogin, navigate])

  const handleLogin = async () => {
    setLoading(true)

    try {
      const formData = new FormData()
      formData.append("username", username)
      formData.append("password", password)

      const response = await axios.post("https://api.rcubedev.tickbox.co.in/api/login/", formData)

      const token = response.data.access || response.data.token
      const returnedClientId = response.data.client_id
      const userName = response.data.username
      const roleId = response.data.role_id
      const companyName = response.data.company_name

      // Generate session ID for this tab
      const sessionId = generateSessionId()

      console.log("Login successful for", userName, "with sessionId:", sessionId)

      // Check if this user is already logged in another tab
      const existingSession = checkExistingSession(userName)
      console.log("Existing session found:", existingSession)

      // Store ALL session data in sessionStorage (tab-specific)
      sessionStorage.setItem("session_id", sessionId)
      sessionStorage.setItem("token", token)
      sessionStorage.setItem("current_username", userName)
      sessionStorage.setItem("user_role_id", roleId)
      sessionStorage.setItem("client_id", returnedClientId || "")
      sessionStorage.setItem("company_name", companyName || "")
      sessionStorage.setItem("is_logged_in", "true")

      // Register this user session in localStorage (this will trigger storage event in other tabs)
      registerUserSession(userName, sessionId)

      toast.current.show({
        severity: "success",
        summary: "Login Successful",
        detail: `Welcome, ${userName}`,
        life: 3000,
      })

      if (onLogin) onLogin()
      navigate("/dashboard")
    } catch (error) {
      const errorMsg = error?.response?.data?.detail || "Invalid credentials or server error"

      toast.current.show({
        severity: "error",
        summary: "Login Failed",
        detail: errorMsg,
        life: 4000,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleForgotSubmit = () => {
    toast.current.show({ severity: "info", summary: "OTP Submitted", detail: `OTP: ${otp}`, life: 2000 })
  }

  return (
    <div
      className="min-h-screen bg-cover bg-center flex items-center justify-center px-4 py-8 relative"
      style={{ backgroundImage: 'url("/Assets/Images/BG2.jpg")' }}
    >
      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-md flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={20} />
        </div>
      )}

      <Toast ref={toast} />

      {/* Logo */}
      <div className="absolute top-1 left-1">
        <img src={logo || "/placeholder.svg"} alt="Logo" className="h-14" />
      </div>

      {/* Login Container */}
      <div className="bg-white/10 backdrop-blur-lg border border-white/30 rounded-2xl p-10 shadow-2xl w-full max-w-md text-white">
        <h1 className="text-3xl font-bold text-center mb-6">
          <span className="text-[#d97925]">R-Cube</span> Login
        </h1>

        {!showForgot ? (
          <>
            {/* Username */}
            <div className="mb-4">
              <label className="block mb-1 font-semibold">Username</label>
              <input
                type="text"
                className="w-full p-3 bg-transparent border border-white/30 rounded-xl placeholder-white/70 text-white focus:ring-2 focus:ring-[#d97925] outline-none"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            {/* Password */}
            <div className="mb-4 relative">
              <label className="block mb-1 font-semibold">Password</label>
              <input
                type={showPassword ? "text" : "password"}
                className="w-full p-3 bg-transparent border border-white/30 rounded-xl placeholder-white/70 text-white focus:ring-2 focus:ring-[#d97925] outline-none"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleLogin()}
              />
              <div
                className="absolute right-3 top-10 cursor-pointer text-[#d97925]"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </div>
            </div>

            <div className="flex justify-between items-center text-sm mb-6">
              <button className="text-white underline" onClick={() => setShowForgot(true)}>
                Forgot Password?
              </button>
            </div>

            <button
              onClick={handleLogin}
              disabled={!username || !password}
              className={`w-full py-3 ${
                username && password ? "bg-[#d97925] hover:scale-105" : "bg-gray-400 cursor-not-allowed"
              } text-black font-bold rounded-xl transition-all`}
            >
              Login
            </button>
          </>
        ) : (
          <>
            {/* Forgot Password Fields */}
            <div className="mb-4">
              <label className="block mb-1 font-semibold">Email</label>
              <input
                type="email"
                className="w-full p-3 bg-transparent border border-white/30 rounded-xl placeholder-white/70 text-white focus:ring-2 focus:ring-[#d97925] outline-none"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-semibold">OTP</label>
              <input
                type="text"
                className="w-full p-3 bg-transparent border border-white/30 rounded-xl placeholder-white/70 text-white focus:ring-2 focus:ring-[#d97925] outline-none"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            </div>
            <button
              onClick={handleForgotSubmit}
              disabled={!otp}
              className={`w-full py-3 ${
                otp ? "bg-[#d97925]" : "bg-gray-400 cursor-not-allowed"
              } text-black font-bold rounded-xl hover:scale-105 transition-all`}
            >
              Submit
            </button>
            <button
              onClick={() => setShowForgot(false)}
              className="text-white text-sm mt-4 underline block text-center"
            >
              Back to Login
            </button>
          </>
        )}

        <p className="text-sm mt-10 text-right">
          &copy; 2025 <span className="text-[#d97925] text-base font-semibold">TickBox Technologies</span>. All Rights
          Reserved.
        </p>
      </div>
    </div>
  )
}

export default Login
